import {jwtDecode} from 'jwt-decode';

// --- Genre de l'utilisateur (utilisé pour les libellés genrés, ex: besoin.states.male_1) ---
export const GENDER_STORAGE_KEY = 'gender';

export const getStoredGender = () => localStorage.getItem(GENDER_STORAGE_KEY);

export const setStoredGender = (gender) => {
  if (gender === 'male' || gender === 'female') {
    localStorage.setItem(GENDER_STORAGE_KEY, gender);
  } else {
    localStorage.removeItem(GENDER_STORAGE_KEY);
  }
};

// Fonction pour la connexion
export const login = async (username, password) => {
  try {
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
      throw new Error('Nom d\'utilisateur ou mot de passe incorrect');
    }

    const data = await response.json();
    localStorage.setItem('token', data.shortToken);
    localStorage.setItem('refreshToken', data.refreshToken);
    setStoredGender(data.gender);

    return { 
      token: data.shortToken, 
      language: data.language || 'en',
      gender: data.gender || null,
      userKey: data.userKey,
      role: data.role,
      expireDate: data.expireDate || null,
      isCluf: data.isCluf || false,
    }; 
  } catch (error) {
    throw error;
  }
};

// Fonction pour l'inscription
export const signup = async (username, email, password, language) => {
  try {
    const response = await fetch('/api/auth/signup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, email, password, language }),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Erreur lors de l\'inscription');
    }

    return 'Inscription réussie ! Vérifiez votre email pour confirmer votre inscription.';
  } catch (error) {
    throw error;
  }
};

// Fonction pour la demande de changement de mot de passe
export const requestResetPassword = async (email) => {
  try {
    const response = await fetch('/api/auth/resetpassword', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Erreur lors de la demande de réinitialisation');
    }

    return 'Un email de réinitialisation a été envoyé';
  } catch (error) {
    throw error;
  }
};

// Fonction pour vérifier que le changer le mot de passe est valide et que la modal doit s'ouvrir
export const verifyResetToken = async (token) => {
  try {
    const response = await fetch(`/api/auth/resetpassword/${token}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
        return { status: authStatus.INVALID };
    }

    const data = await response.json(); // ex: { message: "Token valide", email: "user@mail.com" }
    return { status: authStatus.VALID, emailToken: token, email: data.email };
  } catch (error) {
    return { status: authStatus.INVALID };
  }
};

// Fonction pour changer le mot de passe
export const resetPassword = async (token, newPassword) => {
  try {
    //console.log("Send new password :" + newPassword, token)
    const response = await fetch(`/api/auth/resetpassword/${token}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ newPassword }),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Erreur lors de la réinitialisation du mot de passe.');
    }

    return 'Mot de passe réinitialisé avec succès.';
  } catch (error) {
    throw error;
  }
};

let isRefreshing = false;
let refreshPromise = null;
// ok secure route auth
export const fetchWithAuth = async (url, options = {}) => {
  //console.log("Fetch With Auth");

  options.headers = options.headers || {};

  const result = await checkOrRenewToken();

  if (result.status !== authStatus.VALID) {
    logout();
    throw new Error('Session expirée ou non authentifiée. Veuillez vous reconnecter.');
  }

  options.headers['Authorization'] = `Bearer ${result.token}`;
  options.headers['Content-Type'] = 'application/json';

  let response = null;
  try {
    response = await fetch(url, options);

    if (response.status === 401) {
      console.warn('Token expiré côté serveur, tentative de rafraîchissement...');

      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = refreshAccessToken();
      }

      const refreshResult = await refreshPromise;
      isRefreshing = false;
      refreshPromise = null;

      if (refreshResult.status === authStatus.VALID) {
        // On relance la requête avec le nouveau token
        options.headers['Authorization'] = `Bearer ${refreshResult.token}`;
        response = await fetch(url, options);
      } else {
        logout();
        throw new Error('Session expirée. Veuillez vous reconnecter.');
      }
    }
    } catch (err) {
    console.error('Fetch failed:', err);
    throw new Error('Erreur réseau. Veuillez réessayer.');
  }
  return response;
};

export const fetchOptionalAuth = async (url, options = {}) => {
  let token = null;
  try {
    const result = await checkOrRenewToken();
    if (result.status === authStatus.VALID) {
      token = result.token;
    }
  } catch (e) {
    // pas connecté → token reste null
  }

  if (token) {
    options.headers = { 
      ...options.headers, 
      'Authorization': `Bearer ${token}`, 
      'Content-Type': 'application/json',
    };
  } else {
    options.headers = {
      ...options.headers,
      'Content-Type': 'application/json',
    };
  }

  const response = await fetch(url, options);
  return response;
};

// ok check and update Token -> méthode principale
export const checkOrRenewToken = async () => {
  const status = checkTokenStatus(); 
  
  if (status.status === authStatus.VALID)
  {
    //console.log("checkOrRenewToken", status);
    return { status: authStatus.VALID, token: status.token, gender: getStoredGender() };
  }
  
  if (status.status === authStatus.EXPIRED) {
    const refreshResult = await refreshAccessToken();
    //console.log("renew token -> checkOrRenewToken", status);
    return refreshResult;    
  }
  
  //console.log("checkOrRenewToken", status);
  return { status: status.status };
};

// ok request short token to backend
export const refreshAccessToken = async () => {
  //console.log("request refreshAccessToken to backend");
  const refreshToken = localStorage.getItem('refreshToken');
  
  if (!refreshToken) return { status: authStatus.MISSING };
  
  try {
    const response = await fetch('/api/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      logout();
      return { status: authStatus.INVALID };
    }

    const data = await response.json();

    if (!data.shortToken) {
      logout();
      return { status: authStatus.INVALID };
    }

    localStorage.setItem('token', data.shortToken);

    // Le backend renvoie aussi le genre : on le resynchronise à chaque refresh
    if (data.gender !== undefined) {
      setStoredGender(data.gender);
    }

    return {
      status: authStatus.VALID,
      token: data.shortToken,
      language: data.language,
      gender: data.gender !== undefined ? data.gender : getStoredGender(),
      role: data.role,
      expireDate: data.expireDate || null,
      isCluf: data.isCluf || false,
    };
  } catch (err) {
    logout();
    return { status: authStatus.INVALID };
  }
};

// ok get token status -> AuthStatus
export const checkTokenStatus = () => {  
  const token = localStorage.getItem('token');

  if (!token) return { status: authStatus.MISSING };

  try {
    const decoded = jwtDecode(token);
    const now = Date.now() / 1000;

    if (decoded.exp < now) {
      return { status: authStatus.EXPIRED, token };
    } else {
      return { status: authStatus.VALID, token };
    }
  } catch {
    return { status: authStatus.INVALID };
  }
};

// ok remove storage and logout
export const logout = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem(GENDER_STORAGE_KEY);
};

// ok enum token status
export const authStatus = Object.freeze({
  MISSING: 'missing',
  INVALID: 'invalid',
  EXPIRED: 'expired',
  VALID: 'valid',
});